import {
  type SelectProject,
  insertProjectSchema,
  patchProjectSchema,
} from "@/api/db/schema/project";
import { projectService } from "@/api/services/project.service";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

/**
 * Public: List active projects
 */
export const listActiveProjectsFn = createServerFn({ method: "GET" }).handler(
  async () => {
    try {
      const projects = await projectService.listActiveProjects();
      return { status: 200, body: { projects } };
    } catch (error) {
      console.error("Failed to fetch active projects:", error);
      return {
        status: 500,
        body: {
          projects: [] as SelectProject[],
          error: "Internal Server Error",
        },
      };
    }
  },
);

export const listActiveProjectsOptions = () =>
  queryOptions({
    queryKey: ["projects", "active"],
    queryFn: () => listActiveProjectsFn(),
  });

/**
 * Admin: List all projects (including inactive)
 */
export const listAllProjectsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { error: "Forbidden", projects: [] as SelectProject[] },
      };
    }

    try {
      const projects = await projectService.listAllProjects();
      return { status: 200, body: { projects } };
    } catch (error) {
      console.error("Failed to fetch all projects:", error);
      return {
        status: 500,
        body: {
          error: "Failed to fetch projects",
          projects: [] as SelectProject[],
        },
      };
    }
  });

export const listAllProjectsOptions = () =>
  queryOptions({
    queryKey: ["projects", "all"],
    queryFn: () => listAllProjectsFn(),
  });

/**
 * Admin: Create new project
 */
export const createProjectFn = createServerFn({ method: "POST" })
  .inputValidator(insertProjectSchema)
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { error: "Forbidden" } } as any;
    }

    try {
      const project = await projectService.createProject(data);
      return { status: 201, body: { project } } as any;
    } catch (error) {
      console.error("Failed to create project:", error);
      return {
        status: 500,
        body: { error: "Failed to create project" },
      } as any;
    }
  });

/**
 * Admin: Update project
 */
export const updateProjectFn = createServerFn({ method: "POST" })
  .inputValidator(patchProjectSchema)
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { error: "Forbidden" } } as any;
    }

    try {
      const { projectId, ...updates } = data;
      const project = await projectService.updateProject(projectId, updates);

      if (!project) {
        return { status: 404, body: { error: "Project not found" } } as any;
      }

      return { status: 200, body: { project } } as any;
    } catch (error) {
      console.error("Failed to update project:", error);
      return {
        status: 500,
        body: { error: "Failed to update project" },
      } as any;
    }
  });

/**
 * Admin: Delete project
 */
export const deleteProjectFn = createServerFn({ method: "POST" })
  .inputValidator(z.object({ projectId: z.string() }))
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { error: "Forbidden" } } as any;
    }

    try {
      const project = await projectService.deleteProject(data.projectId);

      if (!project) {
        return { status: 404, body: { error: "Project not found" } } as any;
      }

      return { status: 200, body: { message: "Project deleted" } } as any;
    } catch (error) {
      console.error("Failed to delete project:", error);
      return {
        status: 500,
        body: { error: "Failed to delete project" },
      } as any;
    }
  });

/**
 * Admin: Toggle project active status
 */
export const toggleProjectActiveFn = createServerFn({ method: "POST" })
  .inputValidator(z.object({ projectId: z.string() }))
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return { status: 403, body: { error: "Forbidden" } } as any;
    }

    try {
      const project = await projectService.toggleActive(data.projectId);

      if (!project) {
        return { status: 404, body: { error: "Project not found" } } as any;
      }

      return { status: 200, body: { project } } as any;
    } catch (error) {
      console.error("Failed to toggle project:", error);
      return {
        status: 500,
        body: { error: "Failed to toggle project" },
      } as any;
    }
  });

/**
 * Public: Support a project
 */
export const supportProjectFn = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      projectId: z.string(),
      faculty: z.string().min(1, "Fakülte bilgisi gerekli"),
    }),
  )
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    try {
      const support = await projectService.supportProject(
        context.auth.id,
        data.projectId,
        data.faculty,
      );
      if (!support) {
        return {
          status: 400,
          body: { message: "Bu projeye zaten destek verdiniz." },
        };
      }
      return { status: 201, body: { support } };
    } catch (error) {
      console.error("Failed to support project:", error);
      return {
        status: 500,
        body: { message: "Internal Server Error" },
      };
    }
  });

/**
 * Public: Get support metrics for a project
 */
export const getProjectSupportMetricsFn = createServerFn({ method: "GET" })
  .inputValidator(z.object({ projectId: z.string() }))
  .handler(async ({ data }) => {
    try {
      const metrics = await projectService.getProjectSupportMetrics(
        data.projectId,
      );
      return { status: 200, body: metrics };
    } catch (error) {
      console.error("Failed to fetch project metrics:", error);
      return { status: 500, body: { error: "Internal Server Error" } };
    }
  });

export const getProjectMetricsOptions = (projectId: string) => ({
  queryKey: ["projectMetrics", projectId],
  queryFn: () => getProjectSupportMetricsFn({ data: { projectId } }),
});

/**
 * Public: List unique faculty suggestions
 */
export const listFacultySuggestionsFn = createServerFn({
  method: "GET",
}).handler(async () => {
  try {
    const suggestions = await projectService.listFacultySuggestions();
    return { status: 200, body: { suggestions } };
  } catch (error) {
    console.error("Failed to fetch faculty suggestions:", error);
    return { status: 500, body: { suggestions: [] } };
  }
});

export const listFacultySuggestionsOptions = () =>
  queryOptions({
    queryKey: ["projects", "faculties"],
    queryFn: () => listFacultySuggestionsFn(),
  });
